// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Main component for the company data upload workflow.
// Manages the three-step stepper state (download-template, upload-csv, results)
// and orchestrates the CSV upload process for company data.
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import {
  type CompanyDataUploadResult,
  uploadCompanyData,
  uploadCompanyDataResponseSchema,
} from '@/customer/api/upload-company-data';
import { CsvUpload } from '@/customer/components/company-data-upload/csv-upload';
import { Results } from '@/customer/components/company-data-upload/results';
import { Stepper } from '@/customer/components/company-data-upload/stepper';
import { TemplateDownload } from '@/customer/components/company-data-upload/template-download';
import type {
  ExpandedCompaniesState,
  Step,
} from '@/customer/components/company-data-upload/types';
import { useSupportError } from '@/hooks/use-support-error';
import { Box, CardContent } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface Props {
  onClose: () => void;
}

/**
 * Orchestrates the company data CSV upload workflow through three steps:
 * 1. Download template - shows required/optional fields and provides a CSV template
 * 2. Upload CSV - file selection and upload with progress indication
 * 3. Results - displays created, updated, unchanged, and failed companies
 *
 * @param props.onClose - Callback to close the parent dialog
 */
export function CompanyDataUpload(props: Props) {
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] =
    useState<CompanyDataUploadResult | null>(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedCompanies, setExpandedCompanies] =
    useState<ExpandedCompaniesState>({
      created: false,
      updated: false,
      notUpdated: false,
      failed: false,
      structureErrors: false,
    });
  const [hasFatalError, setHasFatalError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = useCallback(() => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSelectFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUploadResult = useCallback(
    (result: CompanyDataUploadResult) => {
      clearFile();
      setUploadResult(result);
      setUploadErrorMessage(null);
      setCurrentStep('results');
      void queryClient.invalidateQueries({ queryKey: ['customer'] });
    },
    [clearFile, queryClient]
  );

  const { mutate: uploadCompany, isPending: isUploading } = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Missing file selection');
      }
      const formData = new FormData();
      formData.append('data', file);
      return uploadCompanyData(formData);
    },
    onSuccess: (result) => {
      handleUploadResult(result);
    },
    onError: (error: AxiosError) => {
      clearFile();

      if (isPlanLimitError(error)) {
        return;
      }

      // First try to parse as a response with validation errors
      const knownError = uploadCompanyDataResponseSchema.safeParse(
        error.response?.data
      );
      if (knownError.success) {
        const result = knownError.data.data;
        // Only navigate to results if there's actual content to display
        const hasContent =
          result.items.length > 0 ||
          result.structureErrors.length > 0 ||
          result.rowErrors.length > 0;
        if (hasContent) {
          handleUploadResult(result);
          return;
        }
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(error);

      // Curated codes from ERROR_MESSAGES are the handled set: show
      // inline, skip Sentry. Anything that falls through to the support
      // fallback is unhandled and fires Sentry below.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }

      // 5xx or network errors - show global error snackbar
      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while uploading company data.');
    },
  });

  const handleUpload = useCallback(() => {
    if (file) {
      setUploadErrorMessage(null);
      uploadCompany();
    }
  }, [file, uploadCompany]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedCompaniesState) => {
      setExpandedCompanies((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    clearFile();
    setUploadResult(null);
    setUploadErrorMessage(null);
    setExpandedCompanies({
      created: false,
      updated: false,
      notUpdated: false,
      failed: false,
      structureErrors: false,
    });
  }, [clearFile]);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  return (
    <Box>
      <CardContent>
        <Stepper currentStep={currentStep} />

        {currentStep === 'download-template' && (
          <TemplateDownload onBack={props.onClose} onNext={handleNext} />
        )}

        {currentStep === 'upload-csv' && (
          <CsvUpload
            uploadErrorMessage={uploadErrorMessage}
            file={file}
            isUploading={isUploading}
            onSelectFile={handleSelectFile}
            onUpload={handleUpload}
            onBack={handleBack}
            onClose={props.onClose}
            hasFatalError={hasFatalError}
            uploadLineCount={uploadLineCount}
          />
        )}

        {currentStep === 'results' && (
          <Results
            uploadResult={uploadResult}
            expandedCompanies={expandedCompanies}
            onToggleExpanded={handleToggleExpanded}
            onBack={handleBack}
            onStartNew={handleStartNew}
            onClose={props.onClose}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </CardContent>
    </Box>
  );
}
